import _extends from "@babel/runtime/helpers/extends";
import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
import _objectWithoutProperties from "@babel/runtime/helpers/objectWithoutProperties";
import React, { useRef, useState, useMemo, useImperativeHandle, forwardRef } from 'react';
import { useLayoutEffectIsomorphic } from '@chegg-ui/utils';
import { ListboxOptions, ListboxItem, ListboxContainer } from '../styles';
var Listbox = /*#__PURE__*/forwardRef(function (_ref, forwardedRef) {
  var inputId = _ref.inputId,
      options = _ref.options,
      isOpen = _ref.isOpen,
      pseudoFocusedIndex = _ref.pseudoFocusedIndex,
      selectedIndex = _ref.selectedIndex,
      onSelectOption = _ref.onSelectOption,
      _ref$maxHeight = _ref.maxHeight,
      maxHeight = _ref$maxHeight === void 0 ? '200px' : _ref$maxHeight,
      className = _ref.className,
      dataTest = _ref['data-test'],
      parentSpecificProps = _objectWithoutProperties(_ref, ["inputId", "options", "isOpen", "pseudoFocusedIndex", "selectedIndex", "onSelectOption", "maxHeight", "className", "data-test"]);

  var optionsListRef = useRef(null);
  /* istanbul ignore next */

  useImperativeHandle(forwardedRef, function () {
    return optionsListRef.current;
  });
  var focusedOptionRef = useRef(null);

  var _useState = useState(false),
      _useState2 = _slicedToArray(_useState, 2),
      isMouseHovered = _useState2[0],
      setIsMouseHovered = _useState2[1]; // removing hover styles when using keyboard arrows


  var _useState3 = useState(false),
      _useState4 = _slicedToArray(_useState3, 2),
      isDropup = _useState4[0],
      setIsDropup = _useState4[1];
  /*
   * determine if the listbox should drop down or up, according to screen space available
   * only called in response to the user action of opening the listbox, hence safe for SSR
   */


  useLayoutEffectIsomorphic(function () {
    /* istanbul ignore next */
    if (!optionsListRef.current) return;

    if (isOpen) {
      var boundingRect = optionsListRef.current.getBoundingClientRect();
      var windowHeight = window.innerHeight;
      var listHeight = boundingRect.height;
      var spaceAboveDropdown = boundingRect.top;
      var spaceBelowDropdown = windowHeight - boundingRect.top;
      var shouldDropup = spaceBelowDropdown < listHeight && spaceAboveDropdown > spaceBelowDropdown;
      setIsDropup(shouldDropup);
    }
  }, [isOpen]);
  /*
   * scroll focused list option fully into view if needed.
   * only called in response to the user action of opening the listbox or arrowing through, hence safe for SSR
   */

  useLayoutEffectIsomorphic(function () {
    if (isOpen) {
      /* istanbul ignore else */
      if (focusedOptionRef.current) {
        // IE/Safari don't support ScrollIntoViewOptions so we need this check
        // https://developer.mozilla.org/en-US/docs/Web/API/Element/scrollIntoView
        focusedOptionRef.current.scrollIntoView({
          block: 'nearest'
        });
      }

      setIsMouseHovered(false);
    }
  }, [pseudoFocusedIndex, isOpen]);
  return /*#__PURE__*/React.createElement(ListboxContainer, {
    "data-test": "".concat(dataTest ? dataTest : inputId, "_listboxcontainer"),
    isOpen: isOpen && !!options.length,
    dropup: isDropup,
    className: className
  }, /*#__PURE__*/React.createElement(ListboxOptions, _extends({
    id: "".concat(inputId, "_listbox"),
    role: "listbox",
    ref: optionsListRef,
    maxHeight: maxHeight,
    "aria-labelledby": "".concat(inputId, "_label"),
    "aria-activedescendant": parentSpecificProps.tabIndex && pseudoFocusedIndex >= 0 ? "".concat(inputId, "_option_").concat(pseudoFocusedIndex) : undefined,
    "data-test": dataTest
  }, parentSpecificProps), useMemo(function () {
    return (// this wacky typing is to deal with https://stackoverflow.com/a/49511416
      options.map(function (option, index) {
        var isPsuedoFocused = index === pseudoFocusedIndex;
        var isSelected = index === selectedIndex;
        return /*#__PURE__*/React.createElement(ListboxItem, {
          key: "".concat(inputId, "_option_").concat(index),
          id: "".concat(inputId, "_option_").concat(index),
          ref: isPsuedoFocused ? focusedOptionRef : null,
          isFocused: isPsuedoFocused,
          "aria-selected": isPsuedoFocused || undefined,
          role: "option",
          isKeyboardHover: !isMouseHovered,
          isSelected: isSelected,
          value: typeof option === 'string' ? option : option.value,
          onMouseDown: function onMouseDown() {
            onSelectOption(index);
          },
          onMouseMove: function onMouseMove() {
            setIsMouseHovered(true);
          }
        }, typeof option === 'string' ? option : option.child);
      })
    );
  }, [options, pseudoFocusedIndex, isMouseHovered])));
});
Listbox.displayName = 'Listbox';
export default Listbox;


//////////////////
// WEBPACK FOOTER
// ./node_modules/@chegg-ui/listbox/dist/esm/variants/Listbox.js
// module id = 4506
// module chunks = 11